
<template>
  <div class="admin-landing-editor">
    <div class="row">
      <div class="col-md-8">
        <div class="panel panel-bordered" style="overflow: scroll">
          <div class="panel-heading">
            <h3 class="panel-title">Preview</h3>
          </div>
          <div class="panel-body preview-container">
            <div class="preview-wrapper">
              <layout :title="meta.title || 'Page Title'" :footer="false">
                <highlight-form-section
                  v-if="hasSection('highlightFormSection')"
                  :backgroundImage="
                    struct.highlightFormSection.fields.backgroundImage.value
                  "
                  :altImage="struct.highlightFormSection.fields.altImage.value"
                  :title="struct.highlightFormSection.fields.title.value"
                  :subtitle="struct.highlightFormSection.fields.subtitle.value"
                />

                <visual-preview
                  v-if="hasSection('visualPreview')"
                  :title="struct.visualPreview.fields.title.value"
                  :text="struct.visualPreview.fields.text.value"
                  :imageSrc="struct.visualPreview.fields.imageSrc.value"
                  :imageAlt="struct.visualPreview.fields.imageAlt.value"
                  :logos="struct.visualPreview.fields.logos.value"
                />

                <div
                  v-if="hasValidCards"
                  class="w-full mx-auto px-6 sm:px-7 md:px-4 lg:px-10 xl:px-16 max-w-screen-sm md:max-w-screen-2xl py-20"
                >
                  <h2
                    class="text-3xl font-bold text-center mb-12 text-gray-900 px-4 sm:px-6 md:px-8 lg:px-32"
                  >
                    {{ getSectionFieldValue("rowCardsSection", "title", "") }}
                  </h2>

                  <row-cards-section
                    :elements="
                      getSectionFieldValue('rowCardsSection', 'cards', [])
                    "
                  />
                </div>

                <call-to-action
                  v-if="hasSection('callToAction')"
                  :title="struct.callToAction.fields.title.value"
                  :text="struct.callToAction.fields.text.value"
                  :buttonText="struct.callToAction.fields.buttonText.value"
                  :buttonLink="struct.callToAction.fields.buttonLink.value"
                />

                <div v-if="hasSection('targetAudience')" class="py-14">
                  <target-audience
                    :title="struct.targetAudience.fields.title.value"
                    :packages="struct.targetAudience.fields.packages.value"
                    :imageSrc="struct.targetAudience.fields.imageSrc.value"
                    :imageAlt="struct.targetAudience.fields.imageAlt.value"
                    :imageLeft="true"
                  />
                </div>
              </layout>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="panel panel-bordered mb-3">
          <div class="panel-heading">
            <h4 class="panel-title">Meta Information</h4>
          </div>
          <div class="panel-body">
            <div class="form-group">
              <label>Meta Title *</label>
              <input
                v-model="meta.title"
                class="form-control"
                :class="{ 'border-danger': meta.errors.title.length }"
                placeholder="Meta Title"
              />
              <div v-if="meta.errors.title.length" class="error mt-1">
                <div
                  v-for="(err, i) in meta.errors.title"
                  :key="i"
                  class="text-danger small"
                >
                  <i class="fa fa-exclamation-circle"></i> {{ err }}
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>Meta Description</label>
              <input
                v-model="meta.description"
                class="form-control"
                placeholder="Meta Description"
              />
            </div>

            <div class="form-group">
              <label>Slug *</label>
              <input
                v-model="meta.slug"
                class="form-control"
                :class="{ 'border-danger': meta.errors.slug.length }"
                placeholder="slug-da-pagina"
                @blur="formatSlugOnBlur"
              />
              <div v-if="meta.errors.slug.length" class="error mt-1">
                <div
                  v-for="(err, i) in meta.errors.slug"
                  :key="i"
                  class="text-danger small"
                >
                  <i class="fa fa-exclamation-circle"></i> {{ err }}
                </div>
              </div>
              <small class="form-text text-muted">
                Only lowercase letters, numbers and hyphens
              </small>
            </div>

            <div class="form-group">
              <label>Status</label>
              <select v-model="meta.is_active" class="form-control">
                <option :value="true">Active</option>
                <option :value="false">Inactive</option>
              </select>
            </div>

            <div class="form-group">
              <label>Data Form Id</label>
              <input
                v-model="meta.dataFormId"
                class="form-control"
                placeholder="Data Form Id"
              />
            </div>
            <div class="form-group">
              <label>Data Portal Id</label>
              <input
                v-model="meta.dataPortalId"
                class="form-control"
                placeholder="Data Portal Id"
              />
            </div>
          </div>
        </div>
        <div v-if="struct">
          <div
            :key="sectionKey"
            class="panel panel-bordered mb-3"
            v-for="[sectionKey, section] in Object.entries(struct)"
          >
            <div class="panel-heading">
              <h4 class="panel-title">{{ section.header.title }}</h4>
            </div>
            <div class="panel-body">
              <div
                :key="fieldKey"
                class="form-group"
                v-for="[fieldKey, field] in Object.entries(section.fields)"
              >
                <template v-if="field.type !== 'array'">
                  <label> {{ field.label }}</label>

                  <div v-if="field.type === 'text' || field.type === 'url'">
                    <input
                      :class="[
                        field.class,
                        field.errors && field.errors.length
                          ? 'border-danger'
                          : '',
                      ]"
                      :placeholder="field.placeholder"
                      v-model="field.value"
                      :required="sectionKey === 'meta' && fieldKey === 'title'"
                    />
                    <div
                      v-if="field.errors && field.errors.length"
                      class="error mt-1"
                      role="alert"
                    >
                      <div
                        v-for="(err, i) in field.errors"
                        :key="i"
                        class="text-danger small"
                      >
                        <i class="fa fa-exclamation-circle"></i> {{ err }}
                      </div>
                    </div>
                    <small
                      v-if="fieldKey === 'slug' && !field.errors?.length"
                      class="form-text text-muted"
                    >
                      Only lowercase letters, numbers and hyphens (ex:
                      landing-page-one)
                    </small>
                  </div>
                  <div
                    class="form-group"
                    v-else-if="field.type === 'image' || hasImageField(field)"
                  >
                    <div class="image-field-container">
                      <input
                        type="file"
                        :id="`single-image-${sectionKey}-${fieldKey}`"
                        @change="
                          handleSingleImageUpload($event, sectionKey, fieldKey)
                        "
                        accept="image/*"
                        style="display: none"
                      />
                      <button
                        type="button"
                        class="btn-primary btn-sm"
                        @click="triggerSingleImageUpload(sectionKey, fieldKey)"
                      >
                        <i class="fa fa-upload"></i> Upload Image
                      </button>

                      <span class="file-name ml-2">
                        {{
                          getFileNameFromPath(field.value) ||
                          "Nenhum ficheiro selecionado"
                        }}
                      </span>
                    </div>
                  </div>

                  <div class="form-group" v-else-if="field.type === 'gallery'">
                    <div
                      v-for="(item, index) in field.value"
                      :key="index"
                      class="card-item border p-3 mb-2"
                    >
                      <h6>Logo {{ index + 1 }}</h6>

                      <div class="form-group" v-if="hasImageField(item)">
                        <div class="my-2">
                          <input
                            class="w-full"
                            :class="item.class"
                            :placeholder="item.placeholder"
                            v-model="item.imageAlt"
                          />

                          <button
                            type="button"
                            class="btn-primary btn-sm my-2"
                            @click="
                              triggerGalleryUpload(index, sectionKey, fieldKey)
                            "
                          >
                            <i class="fa fa-upload"></i> Upload Image
                          </button>

                          <input
                            type="file"
                            :id="`gallery-${sectionKey}-${fieldKey}-${index}`"
                            @change="
                              handleGalleryImageUpload(
                                $event,
                                index,
                                sectionKey,
                                fieldKey
                              )
                            "
                            accept="image/*"
                            style="display: none"
                          />
                          <span class="file-name ml-2">
                            {{
                              getFileNameFromPath(item.imageSrc) ||
                              "No file selected"
                            }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else-if="field.type === 'select'">
                    <select
                      :class="field.class"
                      v-model="field.value"
                      @change="handleSelectChange(sectionKey, fieldKey, $event)"
                    >
                      <option
                        v-for="option in field.options"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                    <div
                      v-if="field.errors && field.errors.length"
                      class="error mt-1"
                    >
                      <div v-for="(err, i) in field.errors" :key="i">
                        {{ err }}
                      </div>
                    </div>
                  </div>

                  <div v-else>
                    <vue-editor
                      v-model="field.value"
                      :editor-toolbar="customToolbar"
                      :placeholder="field.placeholder"
                    />
                  </div>
                </template>
                <template v-else-if="field.type === 'array'">
                  <label>{{ field.label }}</label>
                  <div
                    v-for="(item, index) in field.value"
                    :key="index"
                    class="card-item border p-3 mb-2"
                  >
                    <h6>Card {{ index + 1 }}</h6>
                    <div class="form-group">
                      <label>Title</label>
                      <input v-model="item.title" class="form-control" />
                    </div>
                    <div class="form-group">
                      <label>Text</label>
                      <vue-editor
                        v-model="item.text"
                        :editor-toolbar="customToolbar"
                        placeholder="Enter card text..."
                      />
                    </div>

                    <div class="space-y-3">
                      <input
                        type="file"
                        :id="`card-${sectionKey}-${fieldKey}-${index}`"
                        @change="
                          handleCardImageUpload(
                            $event,
                            index,
                            sectionKey,
                            fieldKey
                          )
                        "
                        accept="image/*"
                        class="hidden"
                        style="display: none"
                      />

                      <label
                        :for="`card-${sectionKey}-${fieldKey}-${index}`"
                        class="btn-primary btn-sm"
                      >
                        Upload image
                      </label>

                      <span class="file-name ml-2">
                        {{
                          getFileNameFromPath(item.imageSrc) ||
                          "Nenhum ficheiro selecionado"
                        }}
                      </span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <div class="panel panel-bordered">
          <div class="panel-body">
            <div class="btn-group btn-block">
              <button @click="save" class="btn btn-success btn-block">
                <i class="voyager-save"></i> Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from "./../../Components/Layout/LandingPage/Layout.vue";
import HighlightFormSection from "./../../Components/Layout/LandingPage/HighlightFormSection.vue";
import VisualPreview from "./../../Components/Layout/LandingPage/VisualPreview.vue";
import RowCardsSection from "./../../Components/Layout/LandingPage/rowCardsSection.vue";
import CallToAction from "./../../Components/Layout/LandingPage/CallToAction.vue";
import TargetAudience from "./../../Components/Layout/LandingPage/TargetAudience.vue";
import axios from "axios";
import { VueEditor } from "vue2-editor";

export default {
  name: "AdminLandingEditor",
  components: {
    layout: Layout,
    "highlight-form-section": HighlightFormSection,
    "visual-preview": VisualPreview,
    "row-cards-section": RowCardsSection,
    "call-to-action": CallToAction,
    "target-audience": TargetAudience,
    VueEditor,
  },
  props: {
    errors: Object,
  },
  data() {
    return {
      customToolbar: [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
      ],
      meta: {
        title: "",
        description: "",
        slug: "",
        is_active: true,
        dataFormId: "",
        dataPortalId: "",
        slugWasManuallyEdited: false,
        errors: {
          title: [],
          slug: [],
        },
      },
      struct: null,
      uploadHash: null,
      isSaving: false,
    };
  },
  watch: {
    "meta.title"(newValue) {
      if (!this.meta.slugWasManuallyEdited) {
        this.meta.slug = this.generateSlug(newValue);
      }
    },
    "meta.slug"(newValue, oldValue) {
      const autoGeneratedSlug = this.generateSlug(this.meta.title);

      if (oldValue && newValue !== autoGeneratedSlug) {
        this.meta.slugWasManuallyEdited = true;
      }
      this.validateSlugFormat(newValue);
    },
  },
  async mounted() {
    this.uploadHash = crypto.randomUUID();
    try {
      const { data } = await axios.get("/admin/landing-page-structure");
      this.struct = data;

    } catch (error) {
      console.warn("Using standard structure:", error);
    }

    if (window.__LP_INITIAL__ && window.__LP_INITIAL__.id) {
      this.$nextTick(() => {
        setTimeout(() => {
          this.loadExistingData(window.__LP_INITIAL__);
        }, 100);
      });
    }
  },
  computed: {
    isEditMode() {
      return window.__LP_INITIAL__ && window.__LP_INITIAL__.id;
    },
    recordId() {
      return this.isEditMode ? window.__LP_INITIAL__.id : null;
    },

    uploadIdentifier() {
      return this.recordId || this.uploadHash;
    },
    hasValidCards() {
      try {
        const cards = this.struct?.rowCardsSection?.fields?.cards?.value;
        return Array.isArray(cards) && cards.length > 0;
      } catch {
        return false;
      }
    },
  },
  methods: {
    validateSlugFormat(slug) {
      if (!slug) {
        this.meta.errors.slug = [];
        return true;
      }

      const errors = [];

      if (!/^[a-z0-9-]*$/.test(slug)) {
        errors.push("Use only lowercase letters, numbers and hyphens");
      }

      if (slug.startsWith("-") || slug.endsWith("-")) {
        errors.push("Cannot start or end with a hyphen");
      }

      if (slug.includes("--")) {
        errors.push("You cannot have consecutive hyphens");
      }

      if (slug.includes(" ")) {
        errors.push("Cannot contain spaces. Use hyphens.");
      }

      if (/[A-Z]/.test(slug)) {
        errors.push("Use only lowercase letters");
      }

      this.meta.errors.slug = errors;
      return errors.length === 0;
    },

    generateSlug(text) {
      if (!text) {
        return "";
      }

      return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/--+/g, "-")
        .replace(/^-+|-+$/g, "");
    },

    formatSlugOnBlur() {
      const currentSlug = this.meta.slug;
      const formattedSlug = this.generateSlug(currentSlug);

      if (currentSlug !== formattedSlug) {
        this.meta.slug = formattedSlug;

        if (this.$toast) {
          this.$toast.info("Slug formatado automaticamente");
        }
      }
    },

    validateBeforeSave() {
      const errors = [];

      if (!this.meta.title || this.meta.title.trim() === "") {
        errors.push("Meta Title is required!");
        this.meta.errors.title = ["Mandatory field"];
      } else {
        this.meta.errors.title = [];
      }

      if (!this.meta.slug || this.meta.slug.trim() === "") {
        errors.push("Slug is mandatory!");
        this.meta.errors.slug = ["Mandatory field"];
      } else {
        const slugErrors = [];

        if (!/^[a-z0-9-]+$/.test(this.meta.slug)) {
          slugErrors.push("Use only lowercase letters, numbers and hyphens");
        }
        if (this.meta.slug.startsWith("-") || this.meta.slug.endsWith("-")) {
          slugErrors.push("Cannot start or end with a hyphen");
        }
        if (this.meta.slug.includes("--")) {
          slugErrors.push("You cannot have consecutive hyphens");
        }

        if (slugErrors.length > 0) {
          errors.push(`Invalid slug:\n- ${slugErrors.join("\n- ")}`);
          this.meta.errors.slug = slugErrors;
        } else {
          this.meta.errors.slug = [];
        }
      }

      return { isValid: errors.length === 0, errors };
    },

    isTemplateImage(imagePath) {
      return imagePath && imagePath.includes("/_stock/");
    },

    hasImageField(item) {
      return item && item.hasOwnProperty("imageSrc");
    },

    triggerGalleryUpload(index, sectionKey, fieldKey) {
      const fileInput = document.getElementById(
        `gallery-${sectionKey}-${fieldKey}-${index}`
      );
      if (fileInput) {
        fileInput.click();
      }
    },

    async handleGalleryImageUpload(event, itemIndex, sectionKey, fieldKey) {
      const file = event.target.files?.[0];
      if (!file) {
        event.target.value = "";
        return;
      }

      if (!file.type.startsWith("image/")) {
        event.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        event.target.value = "";
        return;
      }

      const galleryArray = this.struct[sectionKey].fields[fieldKey].value;
      const item = galleryArray[itemIndex];

      const formData = new FormData();
      formData.append("image", file);
      formData.append("identifier", this.uploadIdentifier);
      formData.append("is_temp", this.recordId ? "false" : "true");
      formData.append(
        "field_info",
        `${sectionKey}-${fieldKey}-gallery-${itemIndex}`
      );

      if (item.imagePath && !this.isTemplateImage(item.imageSrc)) {
        formData.append("old_path", item.imagePath);
      }

      try {
        const response = await axios.post(
          "/admin/landing-page/upload-image",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (response.data.success) {
          this.$set(item, "imageSrc", response.data.url);
          this.$set(item, "imagePath", response.data.path);
          this.$set(item, "imageName", response.data.filename);
          this.$set(item, "isTemplate", false);

          if (this.$toast) {
            this.$toast.success("Logo uploaded successfully!");
          }
        } else {
          throw new Error(response.data.message || "Upload error");
        }
      } catch (error) {
        const errorMsg =
          error.response?.data?.message || "Failed to upload image";
        if (this.$toast) {
          this.$toast.error(errorMsg);
        } else {
          alert(errorMsg);
        }
      } finally {
        event.target.value = "";
      }
    },

    triggerFileUpload(cardIndex) {
      const fileInput = document.getElementById(`card-file-${cardIndex}`);
      if (fileInput) {
        fileInput.click();
      }
    },

    triggerSingleImageUpload(sectionKey, fieldKey) {
      const fileInput = document.getElementById(
        `single-image-${sectionKey}-${fieldKey}`
      );
      if (fileInput) {
        fileInput.click();
      }
    },

    hasSection(sectionKey) {
      try {
        const section = this.struct?.[sectionKey];
        return (
          section &&
          section.fields &&
          typeof section.fields === "object" &&
          Object.keys(section.fields).length > 0
        );
      } catch {
        return false;
      }
    },

    getMetaValue(fieldKey, defaultValue = "") {
      try {
        return this.struct?.meta?.fields?.[fieldKey]?.value || defaultValue;
      } catch {
        return defaultValue;
      }
    },

    getSectionHeader(sectionKey, defaultValue = "") {
      try {
        return this.struct?.[sectionKey]?.header?.title || defaultValue;
      } catch {
        return defaultValue;
      }
    },

    getSectionFieldValue(sectionKey, fieldKey, defaultValue = "") {
      try {
        const value = this.struct?.[sectionKey]?.fields?.[fieldKey]?.value;

        if (value === null || value === undefined) {
          return defaultValue;
        }

        if (Array.isArray(defaultValue) && !Array.isArray(value)) {
          return defaultValue;
        }

        return value;
      } catch (error) {
        return defaultValue;
      }
    },

    async handleCardImageUpload(event, cardIndex, sectionKey, fieldKey) {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      if (!this.recordId) {
        event.target.value = "";
        return;
      }

      if (!file.type.startsWith("image/")) {
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        return;
      }

      const targetArray = this.struct[sectionKey].fields[fieldKey].value;
      const card = targetArray[cardIndex];

      const formData = new FormData();
      formData.append("image", file);
      formData.append("landing_page_id", this.recordId);

      formData.append("identifier", this.uploadIdentifier);
      formData.append("is_temp", this.recordId ? "false" : "true");
      formData.append("field_info", `${sectionKey}-${fieldKey}-${cardIndex}`);

      if (card.imagePath && !this.isTemplateImage(card.imageSrc)) {
        formData.append("old_path", card.imagePath);
      }
      try {
        const response = await axios.post(
          "/admin/landing-page/upload-image",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (response.data.success) {
          this.$set(card, "imageSrc", response.data.url);
          this.$set(card, "imagePath", response.data.path);
          this.$set(card, "imageName", response.data.filename);
          this.$set(card, "isTemplate", false);

          this.$toast?.success("Image loaded successfully!");
        } else {
          throw new Error(response.data.message || "Upload error");
        }
      } finally {
        event.target.value = "";
      }
    },

    getSectionValue(section, fieldName, defaultValue = "") {
      try {
        if (
          !section ||
          typeof section !== "object" ||
          !section.fields ||
          typeof section.fields !== "object" ||
          !section.fields[fieldName] ||
          typeof section.fields[fieldName] !== "object" ||
          !("value" in section.fields[fieldName])
        ) {
          return defaultValue;
        }

        const value = section.fields[fieldName].value;

        if (value === null || value === undefined) {
          return defaultValue;
        }

        if (Array.isArray(defaultValue)) {
          return Array.isArray(value) ? value : defaultValue;
        }

        if (typeof defaultValue === "string") {
          return typeof value === "string"
            ? value
            : String(value || defaultValue);
        }

        if (typeof defaultValue === "number") {
          const num = Number(value);
          return isNaN(num) ? defaultValue : num;
        }

        if (typeof defaultValue === "boolean") {
          return Boolean(value);
        }

        return value;
      } catch (error) {
        return defaultValue;
      }
    },

    async handleSingleImageUpload(event, sectionKey, fieldKey) {
      const file = event.target.files?.[0];
      if (!file) {
        event.target.value = "";
        return;
      }

      if (!file.type.startsWith("image/")) {
        if (this.$toast) {
          this.$toast.error("Please select a valid image file.");
        }
        event.target.value = "";
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        if (this.$toast) {
          this.$toast.error("The image cannot exceed 5MB");
        }
        event.target.value = "";
        return;
      }

      const formData = new FormData();
      formData.append("image", file);
      formData.append("identifier", this.uploadIdentifier);
      formData.append("is_temp", this.recordId ? "false" : "true");
      formData.append("field_info", `${sectionKey}-${fieldKey}`);

      const field = this.struct[sectionKey].fields[fieldKey];

      if (field.imagePath) {
        formData.append("old_path", field.imagePath);
      }

      try {
        const response = await axios.post(
          "/admin/landing-page/upload-image",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (response.data.success) {
          this.$set(field, "value", response.data.url);
          this.$set(field, "imagePath", response.data.path);
          this.$set(field, "isTemplate", false);

          if (this.$toast) {
            this.$toast.success("Image loaded successfully!");
          }
        } else {
          throw new Error(response.data.message || "Upload error");
        }
      } finally {
        event.target.value = "";
      }
    },

    async save() {
      if (this.isSaving) {
        return;
      }

      this.isSaving = true;

      try {
        const validation = this.validateBeforeSave();

        if (!validation.isValid) {
          const errorMessage = validation.errors.join("\n\n");

          if (this.$toast) {
            this.$toast.error(errorMessage);
          }

          this.$nextTick(() => {
            const errorElement = document.querySelector(".error");
            if (errorElement) {
              errorElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          });

          return;
        }

        const payload = {
          meta_title: this.meta.title.trim(),
          meta_description: this.meta.description || "",
          slug: this.meta.slug.trim(),
          is_active: this.meta.is_active,
          data_form_id: this.meta.dataFormId || null,
          data_portal_id: this.meta.dataPortalId || null,
          content: JSON.stringify({
            highlight_form_section: {
              header: this.struct.highlightFormSection.header,
              fields: this.struct.highlightFormSection.fields,
            },
            visual_preview: {
              header: this.struct.visualPreview.header,
              fields: this.struct.visualPreview.fields,
            },
            call_to_action: {
              header: this.struct.callToAction.header,
              fields: this.struct.callToAction.fields,
            },
            target_audience: {
              header: this.struct.targetAudience.header,
              fields: this.struct.targetAudience.fields,
            },
            row_cards_section: {
              header: this.struct.rowCardsSection.header,
              fields: this.struct.rowCardsSection.fields,
            },
          }),
        };

        if (!this.isEditMode) {
          payload.uploadHash = this.uploadHash;
        }

        const url = this.isEditMode
          ? `/admin/landing-page/${this.recordId}`
          : "/admin/landing-page";

        const method = this.isEditMode ? "put" : "post";

        const response = await axios[method](url, payload, {
          timeout: 30000,
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.data?.success) {
          const message = this.isEditMode
            ? "Landing page updated successfully!"
            : "Landing page created successfully!";

          if (this.$toast) {
            this.$toast.success(message);
          }

          if (!this.isEditMode && response.data.data?.id) {
            window.__LP_INITIAL__ = {
              id: response.data.data.id,
            };

            setTimeout(() => {
              window.location.href = "/admin/landing-pages";
            }, 1500);
          }
        } else {
          throw new Error(response.data?.message || "Invalid server response");
        }
      } catch (error) {
        let errorMessage = "Error saving landing page";

        if (error.response?.status === 422) {
          const errors = error.response.data?.errors || {};
          if (errors.slug) {
            this.meta.errors.slug = errors.slug;
            errorMessage = errors.slug.join(", ");
          } else if (errors.meta_title) {
            this.meta.errors.title = errors.meta_title;
            errorMessage = errors.meta_title.join(", ");
          }
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        if (this.$toast) {
          this.$toast.error(errorMessage);
        }
      } finally {
        this.isSaving = false;
      }
    },

    loadExistingData(data) {
      const content = JSON.parse(data.content || "{}");

      this.meta.title = data.meta_title || "";
      this.meta.description = data.meta_description || "";
      this.meta.slug = data.slug || "";
      this.meta.dataFormId = data.data_form_id || "";
      this.meta.dataPortalId = data.data_portal_id || "";

      if (data.slug) {
        this.meta.slugWasManuallyEdited = true;
      }

      if (data.hasOwnProperty("is_active")) {
        this.meta.is_active = Boolean(data.is_active);
      }

      const sectionMap = {
        highlight_form_section: "highlightFormSection",
        call_to_action: "callToAction",
        visual_preview: "visualPreview",
        target_audience: "targetAudience",
        row_cards_section: "rowCardsSection",
      };

      Object.entries(content).forEach(([key, section]) => {
        const structKey = sectionMap[key];
        if (!structKey || !this.struct?.[structKey]) {
          return;
        }

        const fields = section?.fields || {};
        Object.entries(fields).forEach(([fieldKey, fieldValue]) => {
          if (this.struct[structKey].fields[fieldKey]) {
            this.struct[structKey].fields[fieldKey].value = fieldValue.value;
          }
        });
      });
    },

    loadSectionData(sectionKey, contentSection) {
      if (!contentSection?.fields) {
        return;
      }

      Object.keys(contentSection.fields).forEach((fieldKey) => {
        if (this.struct[sectionKey]?.fields?.[fieldKey]) {
          this.struct[sectionKey].fields[fieldKey].value =
            contentSection.fields[fieldKey].value;
        }
      });
    },

    handleSelectChange(sectionKey, fieldKey, event) {
      const value = event.target.value;

      let parsedValue = value;
      if (value === "true") {
        parsedValue = true;
      }
      if (value === "false") {
        parsedValue = false;
      }

      this.$set(this.struct[sectionKey].fields[fieldKey], "value", parsedValue);
    },

    getFileNameFromPath(path) {
      if (!path) {
        return "";
      }

      const cleanPath = path.split("?")[0];

      const parts = cleanPath.split("/");
      return parts[parts.length - 1];
    },
  },
};
</script>

<style scoped>
.preview-container {
  width: 100%;
  min-width: 1200px;
  overflow-x: auto;
}

.border-danger {
  border-color: #dc3545 !important;
  border-width: 2px !important;
}

.text-danger {
  color: #dc3545;
}

.error {
  margin-top: 0.25rem;
}

.error .small {
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
}

input.border-danger:focus {
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

/* to delete in the future (Vue 3)*/
.admin-landing-editor >>> .relative.z-20 {
  display: flex !important;
  flex-direction: row !important;
  align-items: flex-start !important;
  border: none;
}

.admin-landing-editor >>> .relative.z-20 > div:first-child {
  width: 50% !important;
  flex: 1 !important;
  border: none;
}

.admin-landing-editor >>> .relative.z-20 > div:last-child {
  width: 50% !important;
  flex: 1 !important;
  border: none;
}

.admin-landing-editor >>> .flex-col {
  flex-direction: row !important;
  border: none;
}

.admin-landing-editor >>> .w-full.md\:w-1\/2 {
  width: 50% !important;
  border: none;
}

.admin-landing-editor >>> div.justify-between:nth-child(1) {
  justify-content: center;
}

.admin-landing-editor >>> div.justify-between:nth-child(1) > div:nth-child(2) {
  border: none;
}
</style>